/*
  Le stand cliqué s'affiche sur la carte — 07/09/2026.

  Deux blocs indépendants, reliés par la SECTION qui les contient :
    [data-stand-adresse]  un bloc « Adresse du stand »
    [data-carte-suit]     une carte réglée sur « le stand cliqué »

  POURQUOI LA SECTION, ET PAS UN ANCÊTRE COMMUN

  L'ancienne section « Nos stands » cherchait son iframe DANS la racine qui
  portait aussi les boutons. Ça marchait parce qu'un seul gabarit rendait les
  deux. Ici Quentin compose l'arborescence lui-même : la carte peut être la
  sœur des groupes, ou vivre trois niveaux plus loin. Aucun ancêtre commun
  n'est garanti, sauf la section.

  On remonte donc à `.shopify-section`, et on apparie ce qu'on y trouve. Deux
  cartes dans la même section suivraient le même clic — c'est assumé et sans
  danger : elles montreraient la même chose.

  ⚠️ L'ÉTAT VIT SUR `aria-current`, pas sur une classe décorative : le lecteur
  d'écran annonce le stand affiché, et le CSS s'accroche au même attribut.

  ⚠️ C'EST LE GROUPE QUI EST CLIQUABLE, pas l'adresse seule : le nom, l'adresse
  et les horaires forment une seule cible. On remonte au `.group-block` le plus
  proche ; à défaut, l'adresse elle-même fait la cible.
*/
(function () {
  'use strict';

  var SECTION = '.shopify-section';
  var ADRESSE = '[data-stand-adresse]';
  var CARTE = '[data-carte-suit]';

  /*
    L'adresse place l'epingle, les coordonnees la posent au metre pres.

    ⚠️ LES DEUX ENSEMBLE, PAS L'UNE OU L'AUTRE : la forme `lat,lng (nom)`
    demande a Google le point exact ET lui donne son etiquette. Avec les
    seules coordonnees, l'epingle porte pour nom ses deux nombres.

    ⚠️ LE ZOOM VIENT DE L'IFRAME, pas d'ici. Il etait ecrit en dur dans ce
    fichier ET dans blocks/carte.liquid : deux sources pour un reglage. Le
    `|| '13'` ci-dessous n'est PAS une seconde source, c'est un filet : il ne
    sert que si l'iframe arrive sans l'attribut, ce qu'un thème a jour ne
    produit plus. ⛔ Ne pas s'en servir pour regler le zoom.
  */
  function urlCarte(adresse, coordonnees, zoom) {
    var point = coordonnees
      ? coordonnees + ' (' + adresse + ')'
      : adresse;
    return 'https://maps.google.com/maps?q=' + encodeURIComponent(point) +
      '&z=' + (zoom || '13') + '&output=embed';
  }

  function afficher(section, cible) {
    var cadres = section.querySelectorAll(CARTE);
    if (!cadres.length || !cible) return;

    section.querySelectorAll('[data-stand-cible]').forEach(function (autre) {
      autre.setAttribute('aria-current', autre === cible ? 'true' : 'false');
    });

    var adresse = cible.getAttribute('data-stand-adresse-choisie');
    if (!adresse) return;
    var coordonnees = cible.getAttribute('data-stand-point-choisi');

    cadres.forEach(function (cadre) {
      var url = urlCarte(adresse, coordonnees, cadre.getAttribute('data-carte-zoom'));
      if (cadre.getAttribute('src') !== url) cadre.setAttribute('src', url);
    });
  }

  function brancher(perimetre) {
    var racine = perimetre || document;

    racine.querySelectorAll(ADRESSE).forEach(function (marque) {
      var section = marque.closest(SECTION);
      if (!section) return;

      // La cible cliquable est le groupe qui contient l'adresse ; à défaut,
      // l'adresse elle-même.
      var cible = marque.closest('.group-block') || marque;
      if (cible.dataset.standCible === 'oui') return;

      cible.dataset.standCible = 'oui';
      cible.setAttribute('data-stand-adresse-choisie', marque.getAttribute('data-stand-adresse'));
      var point = marque.getAttribute('data-stand-coordonnees');
      if (point) cible.setAttribute('data-stand-point-choisi', point);
      cible.setAttribute('role', 'button');
      cible.setAttribute('tabindex', '0');
      cible.setAttribute('aria-current', 'false');

      // La couleur du stand affiché est calculée en Liquid — une feuille de
      // styles Shopify ne lit pas la palette. Elle est posée ICI sur le
      // groupe, parce qu'une variable CSS ne remonte jamais à un ancêtre.
      // Le trait et le retrait suivent le même chemin : ils vivent dans les
      // réglages du bloc, pas dans la feuille de styles.
      var couleur = marque.getAttribute('data-stand-couleur');
      if (couleur) cible.style.setProperty('--stand-selection', couleur);

      cible.style.setProperty('--stand-trait', (marque.getAttribute('data-stand-trait') || '0') + 'px');
      cible.style.setProperty('--stand-retrait', (marque.getAttribute('data-stand-retrait') || '0') + 'px');

      // L'itinéraire posé JUSTE APRÈS le groupe (blocks/stand-itineraire,
      // hors du groupe cliquable) s'aligne sur son texte : mêmes mesures,
      // posées sur lui, puisqu'il n'hérite pas de son voisin (08/10/2026).
      var voisin = cible.nextElementSibling;
      if (voisin && voisin.classList.contains('stand-itineraire')) {
        voisin.style.setProperty('--stand-trait', cible.style.getPropertyValue('--stand-trait'));
        voisin.style.setProperty('--stand-retrait', cible.style.getPropertyValue('--stand-retrait'));
        voisin.setAttribute('data-stand-aligne', '');
      }

      cible.addEventListener('click', function () {
        afficher(section, cible);
      });

      // Un élément rendu cliquable au clavier doit répondre à Entrée et à la
      // barre d'espace, comme un vrai bouton.
      cible.addEventListener('keydown', function (evenement) {
        if (evenement.key === 'Enter' || evenement.key === ' ') {
          evenement.preventDefault();
          afficher(section, cible);
        }
      });
    });

    // Au chargement, chaque section affiche son premier stand : sans cela la
    // carte réglée sur « le stand cliqué » resterait vide.
    racine.querySelectorAll(SECTION).forEach(function (section) {
      if (!section.querySelector(CARTE)) return;
      var premier = section.querySelector('[data-stand-cible]');
      if (premier) afficher(section, premier);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      brancher();
    });
  } else {
    brancher();
  }

  // L'éditeur visuel recharge une section sans recharger la page.
  document.addEventListener('shopify:section:load', function (evenement) {
    brancher(evenement.target);
  });
})();
